import {
  Component, OnInit, OnDestroy,
  inject, signal, computed, effect, untracked,
  ViewChild, TemplateRef,
  ElementRef, HostListener,
} from '@angular/core';
import { CommonModule }           from '@angular/common';
import { FormsModule }            from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { toSignal }               from '@angular/core/rxjs-interop';
import { map }                    from 'rxjs/operators';
import { TreeNode, TreeNodeEvent } from '@libs/front/components/tree-view/tree-view.component';

import { NavLeftService }        from 'src/app/services/nav-left.service';
import { PageBarService }        from 'src/app/services/page-bar.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { SurveyService }              from 'src/app/services/survey.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { UtilisateurService }         from 'src/app/services/utilisateur.service';
import { ButtonComponent }       from '@libs/front/components/button/button.component';
import { InputComponent }        from '@libs/front/components/input/input.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { CartePopupComponent }    from '@libs/front/components/popup-panel/popup-panel.component';
import { SiteDashboardComponent } from 'src/app/features/site/dashboard/site-dashboard.component';
import { SiteFilesComponent }     from 'src/app/features/site/files/site-files.component';
import { PlanningComponent }      from 'src/app/shared/components/planning/planning.component';
import { DrillColorBy, PlanningMode, TimeScale } from 'src/app/shared/components/planning/planning.types';
import { SiteAnalysisDataComponent } from 'src/app/features/site/analysis-data/site-analysis-data.component';
import { Projet }                 from 'src/app/bo/projet';
import { ScrollableComponent } from 'src/app/shared/components/scrollable/scrollable.component';

import {
  TreeGroupingConfig,
  GroupingLevel,
  GROUPING_OPTIONS_L1,
  GROUPING_OPTIONS_L2,
  GROUPING_OPTIONS_L3,
} from 'src/app/shared/models/tree-grouping.types';
import { SiteTreeFilters, SiteTreeStatusFilter, nodeKeyFromUrl } from 'src/app/services/new_services/site-navigation.service';

// import { ProjectDashboardComponent }   from 'src/app/features/project/project-dashboard/project-dashboard.component';
// import { ProjectFilesComponent }       from 'src/app/features/project/project-files/project-files.component';
// import { NodeDashboardComponent }      from 'src/app/features/node/node-dashboard/node-dashboard.component';
import { BlastComponent } from 'src/app/features/blast/blast.component';
import { LegacySiteContextService }  from 'src/app/services/new_services/legacy-site-context.service';
import { LegacyBlastActionsService } from 'src/app/shared/legacy/blast/legacy-blast-actions.service';
import { LegacySiteActionsService }  from 'src/app/shared/legacy/site/legacy-site-actions.service';
import { PopupComponent }            from '@libs/front/components/popup/popup.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BLAST_TABS, isBlastTabEnabled } from 'src/app/features/blast/blast-tabs';
import { SiteMapComponent } from 'src/app/features/site/site-map/site-map.component';
import { CONFIG_SECTION_ROLES } from 'src/app/features/config/config-roles';
import { SETTINGS_DETAIL } from 'src/app/features/config/config-shared/config-shared.utils';
import { FocusBlastRequest } from 'src/app/features/site/site-map/site-map.types';

// ── Tabs ──────────────────────────────────────────────────────────────────────

const SITE_TABS = [
  { id: 'maps',              label: 'GLOBAL.CARTE',              dataAttr: 'site-tab-site-maps'              },
  { id: 'dashboard',         label: 'GLOBAL.DASHBOARD',         dataAttr: 'site-tab-site-dashboard'         },
  { id: 'planning',          label: 'GLOBAL.PLANNING',          dataAttr: 'site-tab-site-planning'          },
  { id: 'files',             label: 'GLOBAL.FICHIERS',             dataAttr: 'site-tab-site-files'             },
];

const PROJECT_TABS = [
  { id: 'maps',  label: 'GLOBAL.CARTE',  dataAttr: 'site-tab-project-maps'  },
  { id: 'kpi',   label: 'GLOBAL.DONNEES_ANALYSE',   dataAttr: 'site-tab-project-kpi'   },
  { id: 'files', label: 'GLOBAL.FICHIERS', dataAttr: 'site-tab-project-files' },
];

/** Onglet carte propre à l'écran tir : il n'est pas dans BLAST_TABS car il n'est pas rendu par app-blast. */
const BLAST_MAP_TAB = { id: 'maps', label: 'GLOBAL.CARTE', dataAttr: 'site-tab-blast-maps' };

const NODE_TABS = [
  { id: 'kpi', label: 'GLOBAL.DONNEES_ANALYSE', dataAttr: 'site-tab-node-kpi' },
];


const GROUPING_LEVEL_KEYS: Record<GroupingLevel, string> = {
  project: 'GLOBAL.PROJET',
  zone:    'GLOBAL.ZONE',
  type:    'TREEVIEW.TYPE_TIR',
  niveau:  'GLOBAL.NIVEAU',
  year:    'TREEVIEW.ANNEE',
  month:   'TREEVIEW.MONTH',
  none:    'TREEVIEW.AUCUN',
};

function clampSelection(current: string[], available: string[]): string[] {
  if (!available.length) return [];
  if (!current.length) return [...available];
  return current.filter(item => available.includes(item));
}

function withOpenBadges(nodes: TreeNode[]): TreeNode[] {
  return nodes.map(node => node.type === 'blast'
    ? { ...node, badge: 'TREEVIEW.OPEN' }
    : { ...node, children: node.children ? withOpenBadges(node.children) : undefined });
}

function collectBlastIds(node: TreeNode): number[] {
  if (node.type === 'blast') return [Number(node.id)];
  return (node.children ?? []).flatMap(collectBlastIds);
}

/** Retrouve le nœud de regroupement de l'URL, dont l'id vaut « type:valeur » ou « valeur~projetId » (cf. SiteNavigationService). */
function findGroupNode(nodes: TreeNode[], type: string, value: string, projectId: number | null): TreeNode | null {
  for (const node of nodes) {
    if (node.type === type && (node.id === `${type}:${value}` || node.id === `${value}~${projectId}`)) return node;
    const found = findGroupNode(node.children ?? [], type, value, projectId);
    if (found) return found;
  }
  return null;
}

/** « Tout coché » n'est pas un filtre : le stocker tel quel figerait les noms de projets /
 *  zones / types du site courant, qui masqueraient ensuite tous les tirs du site suivant.
 *  Une sélection vide veut déjà dire « tout », donc on la normalise. */
function normalizeSelection(selected: string[], available: string[]): string[] {
  return selected.length >= available.length ? [] : selected;
}

// ── Composant ─────────────────────────────────────────────────────────────────

@Component({
  selector:    'app-new-site',
  standalone:  true,
  templateUrl: './new-site.component.html',
  styleUrls:   ['./new-site.component.scss'],
  imports: [
    CommonModule, FormsModule, TranslateModule,
    ButtonComponent, PopupComponent, InputComponent, SelectComponent, CartePopupComponent,
    SiteDashboardComponent,
    SiteFilesComponent,
    PlanningComponent,
    SiteAnalysisDataComponent,
    // ProjectDashboardComponent, ProjectFilesComponent,
    // NodeDashboardComponent,
    BlastComponent,
    ScrollableComponent,
    SiteMapComponent,
  ],
})
export class NewSiteComponent implements OnInit, OnDestroy {

  private readonly route   = inject(ActivatedRoute);
  readonly         router  = inject(Router);
  readonly         navLeft = inject(NavLeftService);
  readonly         pageBar = inject(PageBarService);
  readonly         siteNav = inject(SiteNavigationService);
  readonly         legacy       = inject(LegacySiteContextService);
  readonly         blastActions = inject(LegacyBlastActionsService);
  readonly         siteActions  = inject(LegacySiteActionsService);
  private readonly surveyService              = inject(SurveyService);
  private readonly keycloakUtilisateurService = inject(KeycloakUtilisateurService);
  private readonly utilisateurService         = inject(UtilisateurService);
  private readonly translate                  = inject(TranslateService);
  private readonly lang                       = toSignal(this.translate.onLangChange.pipe(map(e => e.lang)), { initialValue: this.translate.currentLang });
  private readonly projectWord                = toSignal(this.translate.stream('GLOBAL.PROJET'), { initialValue: '' });

  // ── ViewChild — résolus avant ngOnInit (static: true) ────────────────────
  @ViewChild('headerTpl',         { static: true }) private headerTpl!:         TemplateRef<any>;
  @ViewChild('searchTpl',         { static: true }) private searchTpl!:         TemplateRef<any>;
  @ViewChild('siteCardTpl',       { static: true }) private siteCardTpl!:       TemplateRef<any>;
  @ViewChild('pageBarSiteTpl',    { static: true }) private pageBarSiteTpl!:    TemplateRef<any>;
  @ViewChild('pageBarProjectTpl', { static: true }) private pageBarProjectTpl!: TemplateRef<any>;
  @ViewChild('pageBarNodeTpl',    { static: true }) private pageBarNodeTpl!:    TemplateRef<any>;
  @ViewChild('pageBarBlastTpl',   { static: true }) private pageBarBlastTpl!:   TemplateRef<any>;
  @ViewChild('filterBtn') private filterBtnRef?: ElementRef<HTMLElement>;

  // ── Route ─────────────────────────────────────────────────────────────────

  /** Site of the route; the shell is reused across `/new-site/:siteId`, so read it as a signal. */
  readonly routeSiteId = toSignal(
    this.route.paramMap.pipe(map(p => Number(p.get('siteId')) || null)),
    { initialValue: Number(this.route.snapshot.paramMap.get('siteId')) || null },
  );

  // ── QueryParams ───────────────────────────────────────────────────────────

  readonly queryParams = toSignal(
    this.route.queryParamMap.pipe(
      map(p => ({
        projectId: p.get('projectId') ? Number(p.get('projectId')) : null,
        blastId:   p.get('blastId')   ? Number(p.get('blastId'))   : null,
        nodeType:  p.get('nodeType')  ?? null,
        nodeValue: nodeKeyFromUrl(p.get('nodeType'), p.get('nodeValue')),
        tab:       p.get('tab')       ?? null,
        planning:  p.get('planning')  ?? null,
        scale:     p.get('scale')     ?? null,
        date:      p.get('date')      ?? null,
        colorBy:   p.get('colorBy')   ?? null,
      }))
    ),
    { initialValue: { projectId: null, blastId: null, nodeType: null, nodeValue: null, tab: null, planning: null, scale: null, date: null, colorBy: null } }
  );

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly activeScreen = computed(() => {
    const p = this.queryParams();
    if (p.blastId)                return 'blast'   as const;
    if (p.nodeType === 'project') return 'project' as const;
    if (p.nodeType)               return 'node'    as const;
    if (p.projectId)              return 'project' as const;
    return 'site' as const;
  });

  /** Le formulaire de feedback est défini au niveau région : sans droit de lecture ni
   *  d'écriture sur le customerSurvey, l'onglet Feedback n'existe pas (cf. ancien front). */
  readonly hasCustomerSurveyAccess = signal(false);
  readonly hasSupplierSurveyAccess = signal(false);

  /** The Holes tab merges the legacy design / loading / 3D / deliveries tabs :
   *  it is shown as soon as the site enables at least one of them. */
  readonly hasHolesTabAccess = computed(() => {
    const features = this.siteNav.site()?.features;
    if (!features) return true;
    return features.showBlastDesignTab
        || features.showBlastLoadTab
        || features.show3dViewerTab
        || features.showDeliveriesTab;
  });

  readonly activeTabs = computed(() => {
    switch (this.activeScreen()) {
      case 'blast':   return [BLAST_MAP_TAB, ...BLAST_TABS.filter(t =>
                        isBlastTabEnabled(t, this.siteNav.site()?.features) &&
                        (t.id !== 'holes'             || this.hasHolesTabAccess()) &&
                        (t.id !== 'feedback'          || this.hasCustomerSurveyAccess()) &&
                        (t.id !== 'supplier-feedback' || this.hasSupplierSurveyAccess()))];
      case 'project': return PROJECT_TABS;
      case 'node':    return NODE_TABS;
      default:        return SITE_TABS;
    }
  });

  // Planning view, kept in the URL (`planning`, `scale`, `date`, `colorBy`): the Holes toolbar
  // opens either sub-view, and Back from a blast opened from a cell lands on the same period.
  readonly planningMode = computed<PlanningMode>(() =>
    this.queryParams().planning === 'delivery' ? 'delivery' : 'drilling');
  readonly planningScale = computed<TimeScale>(() =>
    this.queryParams().scale === 'month' ? 'month' : 'week');
  readonly planningColorBy = computed<DrillColorBy>(() =>
    this.queryParams().colorBy === 'operator' ? 'operator' : 'rig');
  readonly planningDate = computed(() => this.queryParams().date);

  setPlanningParams(params: { planning?: PlanningMode; scale?: TimeScale; colorBy?: DrillColorBy; date?: string | null }): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         params,
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  readonly pageBarLabel = computed(() => {
    const p = this.queryParams();
    switch (this.activeScreen()) {
      case 'blast':   return '';   // the tabs alone identify the blast; its name lives in the treeview / folded rail
      case 'project': return `${this.projectWord()} ${this.analysisProject()?.nom ?? p.nodeValue ?? ''}`.trim();
      case 'node':    return this.siteNav.nodeLabel(p.nodeType, p.nodeValue);
      default:        return this.siteNav.siteName();
    }
  });

  readonly analysisProject = computed<Projet | null>(() => {
    const projectId = this.queryParams().projectId;
    const blast = projectId ? this.siteNav.blasts().find(b => b.projetId === projectId) : undefined;
    return blast ? ({ projetId: projectId, nom: blast.nomProjet } as Projet) : null;
  }, { equal: (a, b) => a?.projetId === b?.projetId && a?.nom === b?.nom });

  readonly analysisBlastIds = computed<number[]>(() => {
    const { nodeType, nodeValue, projectId } = this.queryParams();
    if (this.activeScreen() !== 'node' || !nodeType || !nodeValue) return [];
    const node = findGroupNode(this.siteNav.treeData(), nodeType, nodeValue, projectId);
    return node ? collectBlastIds(node) : [];
  }, { equal: (a, b) => a.join(',') === b.join(',') });

  /** Texte du rail quand le nav-left est replié : le nom du blast ouvert, sinon le label courant. */
  readonly navLeftCollapsedLabel = computed(() => {
    const p = this.queryParams();
    if (this.activeScreen() === 'blast' && p.blastId) {
      return this.siteNav.blastNameById().get(p.blastId) ?? `Blast ${p.blastId}`;
    }
    return this.pageBarLabel();
  });

  readonly searchText = signal('');

  /** Onglet carte du site ou d'un projet : l'arbre y pilote la carte comme sur /area (clic = sélection, « Open » = onglets du tir). */
  readonly isMapMode = computed(() =>
    (this.activeScreen() === 'site' || this.activeScreen() === 'project') &&
    this.pageBar.config().activeTabId === 'maps'
  );

  readonly mapFocus = signal<FocusBlastRequest | null>(null);

  /**
   * Sur l'écran tir, la carte ne montre que le tir ouvert ; sur l'écran projet, les tirs du projet sélectionné
   * (filtres et cases de l'arbre toujours appliqués).
   */
  readonly mapVisibleBlastIds = computed<number[]>(() => {
    const { projectId, blastId } = this.queryParams();
    if (this.activeScreen() === 'blast' && blastId) return [blastId];
    const visibleIds = this.siteNav.visibleBlastIds();
    if (this.activeScreen() !== 'project' || !projectId) return visibleIds;
    const projectBlastIds = new Set(
      this.siteNav.blasts().filter(blast => blast.projetId === projectId).map(blast => blast.tirId)
    );
    return visibleIds.filter(id => projectBlastIds.has(id));
  });

  /** Tir sur lequel cadrer la carte : celui de l'écran tir, uniquement sur l'onglet Maps (la carte masquée n'a pas de taille). */
  private readonly blastToFit = computed<number | null>(() =>
    this.activeScreen() === 'blast' && this.pageBar.config().activeTabId === BLAST_MAP_TAB.id
      ? this.queryParams().blastId
      : null
  );

  /** Rôle requis par le bouton « Créer un tir » de l'ancienne treeview. Le projet est résolu au clic. */
  readonly canCreateBlast = computed(() =>
    this.legacy.ready() &&
    this.keycloakUtilisateurService.hasRole('create_blast')
  );

  readonly canCreateProject = computed(() => this.keycloakUtilisateurService.hasRole('create_project'));

  /**
   * Le projet sélectionné dans l'arbre est proposé par défaut ; openCreate le remplace par un projet
   * ouvert du site s'il est terminé.
   */
  onCreateBlast(): void {
    if (!this.canCreateBlast()) return;
    this.blastActions.openCreate(this.siteNav.projectId());
  }

  /** Même rôle que la section « Sites » de la configuration v2. */
  readonly canOpenSiteSettings = computed(() =>
    this.siteNav.siteId() != null &&
    this.keycloakUtilisateurService.hasAnyRole([...CONFIG_SECTION_ROLES.sites])
  );

  /** Ouvre la fiche du site courant dans la configuration v2, dans l'organisation du site. */
  openSiteSettings(): void {
    if (!this.canOpenSiteSettings()) return;
    const orgId = this.siteNav.orgId();
    this.router.navigate(['/new_config', 'sites', String(this.siteNav.siteId()), SETTINGS_DETAIL], {
      queryParams: orgId ? { orgId } : {},
    });
  }

  // ── Filter popup ──────────────────────────────────────────────────────────

  readonly filterOpen     = signal(false);
  readonly filterPopupPos = signal({ top: '0px', right: '0px' });
  readonly popupTab = signal<'filtrage' | 'parametres'>('filtrage');

  readonly pendingActiveProjectsOnly = signal(false);
  readonly pendingActiveBlastsOnly = signal(false);
  readonly pendingProjects = signal<string[]>([]);
  readonly pendingZones = signal<string[]>([]);
  readonly pendingTypes = signal<string[]>([]);
  readonly pendingStatus = signal<SiteTreeStatusFilter>('all');
  readonly pendingLevel = signal<string | null>(null);
  readonly pendingStartDate = signal<string>('');
  readonly pendingEndDate = signal<string>('');

  readonly filterOptions = computed(() => this.siteNav.filterOptions());
  readonly statusOptions = computed<SelectOption[]>(() => this.translateLabels([
    { id: 'all', label: 'OPERATIONS.STATUS_ALL' },
    { id: 'active', label: 'GLOBAL.ACTIF' },
    { id: 'completed', label: 'OPERATIONS.STATUS_COMPLETED' },
  ]));
  readonly levelOptions = computed<SelectOption[]>(() =>
    this.filterOptions().levels.map(level => ({ id: level, label: level }))
  );
  readonly projectFilterOptions = computed<SelectOption[]>(() =>
    this.filterOptions().projects.map(project => ({ id: project, label: project }))
  );
  readonly zoneFilterOptions = computed<SelectOption[]>(() =>
    this.filterOptions().zones.map(zone => ({ id: zone, label: zone }))
  );
  readonly typeFilterOptions = computed<SelectOption[]>(() =>
    this.filterOptions().types.map(type => ({ id: type, label: type }))
  );

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filterOpen()) return;
    const wrapper = this.filterBtnRef?.nativeElement;
    if (!wrapper) return;
    if (!wrapper.contains(event.target as Node)) {
      this.filterOpen.set(false);
      this._syncPendingFiltersWithStored();
      this._syncPendingGroupingWithStored();
    }
  }

  openFilter(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    this.filterPopupPos.set({
      top:   `${rect.bottom + 4}px`,
      right: `${window.innerWidth - rect.right}px`,
    });

    if (!this.filterOpen()) {
      this._syncPendingGroupingWithStored();
      this._syncPendingFiltersWithStored();
    }

    this.filterOpen.set(!this.filterOpen());
  }

  applyGrouping(): void {
    this.confirmSettings();
    this.filterOpen.set(false);
  }

  changerVuePopup(vue: 'filtrage' | 'parametres'): void {
    this.popupTab.set(vue);
  }

  applyPopup(): void {
    if (this.popupTab() === 'filtrage') {
      this.applyFilters();
    } else {
      this.confirmSettings();
    }
    this.filterOpen.set(false);
  }

  resetPopup(): void {
    if (this.popupTab() === 'filtrage') {
      this.resetFilters();
      return;
    }
    this.siteNav.resetGrouping();
    this._syncPendingGroupingWithStored();
  }

  // ── Settings ──────────────────────────────────────────────────────────────

  readonly pendingL1 = signal<GroupingLevel>(this.siteNav.grouping().level1);
  readonly pendingL2 = signal<GroupingLevel>(this.siteNav.grouping().level2);
  readonly pendingL3 = signal<GroupingLevel>(this.siteNav.grouping().level3);

  private translateLabels(options: SelectOption[]): SelectOption[] {
    this.lang();
    return options.map(o => ({ ...o, label: this.translate.instant(o.label) }));
  }

  private toOptions(levels: GroupingLevel[]): SelectOption[] {
    return this.translateLabels(levels.map(l => ({ id: l, label: GROUPING_LEVEL_KEYS[l] })));
  }

  readonly optionsL1 = computed(() => this.toOptions(GROUPING_OPTIONS_L1));
  readonly optionsL2 = computed(() =>
    this.toOptions(GROUPING_OPTIONS_L2.filter(l => l === 'none' || l !== this.pendingL1()))
  );
  readonly optionsL3 = computed(() => {
    if (this.pendingL2() === 'none') return [];
    return this.toOptions(
      GROUPING_OPTIONS_L3.filter(
        l => l === 'none' || (l !== this.pendingL1() && l !== this.pendingL2())
      )
    );
  });
  readonly level3Disabled = computed(() => this.pendingL2() === 'none');

  // ── Constructeur — uniquement les effects sans ViewChild ──────────────────

  constructor() {
    // The shell owns the routed site, but the tabs it hosts (Holes, KPI…) and the legacy
    // services they reuse read `UtilisateurService.currentSite()`: keep it — and the active
    // area — on the site of the route, or they answer for the previously visited site.
    effect(() => {
      const siteId = this.routeSiteId();
      if (!siteId) return;

      untracked(() => {
        if (this.siteNav.siteId() !== siteId) {
          this.siteNav.setSite(siteId);
        }
        this.utilisateurService.setCurrentSiteById(siteId);
      });
    });

    // Synchronise queryParams → selectedNodeId (pas de ViewChild)
    effect(() => {
      this.siteNav.syncFromQueryParams(this.queryParams());
    });

    // Droits sur le formulaire de feedback de la région → visibilité de l'onglet Feedback
    effect(() => {
      const regionId = this.siteNav.regionId();
      if (!regionId) {
        this.hasCustomerSurveyAccess.set(false);
        this.hasSupplierSurveyAccess.set(false);
        return;
      }
      const userRoles = this.keycloakUtilisateurService.allRoles;
      const hasAccess = (permission: any): boolean => {
        const canRead  = permission?.read?.some((r: string) => userRoles.includes(r)) ?? false;
        const canWrite = permission?.write?.some((r: string) => userRoles.includes(r)) ?? false;
        return canRead || canWrite;
      };
      this.surveyService.getSurveyQuestionsJsonData(regionId)
        .then(form => {
          this.hasCustomerSurveyAccess.set(hasAccess(form?.customerSurvey?.permission));
          this.hasSupplierSurveyAccess.set(hasAccess(form?.providerSurvey?.permission));
        })
        .catch(() => {
          this.hasCustomerSurveyAccess.set(false);
          this.hasSupplierSurveyAccess.set(false);
        });
    });

    // Met à jour treeData + selectedNodeId dans NavLeftService
    // Ces deux signals viennent du service, pas des ViewChild → OK dans le constructeur
    effect(() => {
      const treeData = this.siteNav.treeData();
      this.navLeft.setTreeData(this.isMapMode() ? withOpenBadges(treeData) : treeData);
    });

    effect(() => {
      this.navLeft.setSelectedNodeId(this.siteNav.selectedNodeId());
    });

    effect(() => {
      this.navLeft.setSearchText(this.searchText());
    });

    // Met à jour le label de la page-bar quand le nom du site arrive
    effect(() => {
      this.pageBar.setLabel(this.pageBarLabel());
    });

    // Nom écrit verticalement dans le rail quand le nav-left est replié
    effect(() => {
      this.navLeft.setCollapsedLabel(this.navLeftCollapsedLabel());
    });

    // Met à jour les boutons d'actions quand l'écran change
    effect(() => {
      this.pageBar.setActionsTemplate(this._getActionsTemplate());
    });

    // Recadre la carte sur le tir à chaque changement de tir ou retour sur l'onglet Maps
    effect(() => {
      const blastId = this.blastToFit();
      if (blastId === null) return;
      this.mapFocus.update(current => ({ blastId, sequence: (current?.sequence ?? 0) + 1, mode: 'fit' }));
    });

    // Sync tabs + tab actif depuis l'URL
    effect(() => {
      const tabs   = this.activeTabs();
      const urlTab = this.queryParams().tab;
      const tabId  = urlTab && tabs.some(t => t.id === urlTab) ? urlTab : tabs[0]?.id ?? '';
      this.pageBar.setTabs(tabs);
      this.pageBar.setActiveTab(tabId);
    });
  }

  // ── ngOnInit — config statique avec ViewChild (garantis résolus ici) ──────

  ngOnInit(): void {
    // Config statique nav-left : templates + callbacks uniquement (treeData géré par effect)
    this.navLeft.set({
      treeData:         [],
      selectParent:     false,
      collapsible:      true,
      collapsedLabel:   this.navLeftCollapsedLabel(),
      headerTemplate:   this.headerTpl,
      searchTemplate:   this.searchTpl,
      siteCardTemplate: this.siteCardTpl,
      onNodeClick:      (e) => this.onNodeClick(e),
      onBadgeClick:     (e) => this.onBadgeClick(e),
      onCheckboxChange: (e) => this.siteNav.setBlastsChecked(collectBlastIds(e.node), e.checked ?? true),
      onSelectAll:      (checked) => this.siteNav.setAllBlastsChecked(checked),
    });

    // Config statique page-bar : templates d'actions + callbacks
    this.pageBar.set({
      label:           this.pageBarLabel(),
      backLabel:       'OPERATIONS.BACK_AREA_SITES',
      tabs:            this.activeTabs(),
      // The site screen has no tab (SITE_TABS is empty): `[0].id` threw here and left the page bar
      // without its callbacks, so clicking a tab no longer navigated.
      activeTabId:     this.activeTabs()[0]?.id,
      actionsTemplate: this._getActionsTemplate(),
      onTabClick:      (tab) => this.router.navigate([], {
        relativeTo:          this.route,
        // Planning's view only means something on that tab.
        queryParams:         tab.id === 'planning'
          ? { tab: tab.id }
          : { tab: tab.id, planning: null, scale: null, date: null, colorBy: null },
        queryParamsHandling: 'merge',
      }),
      onBack:          () => this.router.navigate(['/area']),
    });
  }

  ngOnDestroy(): void {
    this.navLeft.reset();
    this.pageBar.reset();
  }

  // ── Handlers settings ─────────────────────────────────────────────────────

  onL1Change(option: SelectOption | null): void {
    if (!option) return;
    const val = option.id as GroupingLevel;
    this.pendingL1.set(val);
    if (this.pendingL2() === val) this.pendingL2.set('none');
    if (this.pendingL3() === val) this.pendingL3.set('none');
  }

  onL2Change(option: SelectOption | null): void {
    const val = (option?.id ?? 'none') as GroupingLevel;
    this.pendingL2.set(val);
    if (val === 'none') {
      this.pendingL3.set('none');
    } else if (this.pendingL3() === val) {
      this.pendingL3.set('none');
    }
  }

  onL3Change(option: SelectOption | null): void {
    this.pendingL3.set((option?.id ?? 'none') as GroupingLevel);
  }

  confirmSettings(): void {
    const config: TreeGroupingConfig = {
      level1: this.pendingL1() as Exclude<GroupingLevel, 'none'>,
      level2: this.pendingL2(),
      level3: this.pendingL3(),
    };
    this.siteNav.setGrouping(config);
  }

  applyFilters(): void {
    const options = this.siteNav.filterOptions();
    const next: SiteTreeFilters = {
      activeProjectsOnly: this.pendingActiveProjectsOnly(),
      activeBlastsOnly: this.pendingActiveBlastsOnly(),
      projects: normalizeSelection(this.pendingProjects(), options.projects),
      zones: normalizeSelection(this.pendingZones(), options.zones),
      types: normalizeSelection(this.pendingTypes(), options.types),
      status: this.pendingStatus(),
      level: this.pendingLevel(),
      startDate: this.pendingStartDate() || null,
      endDate: this.pendingEndDate() || null,
    };
    this.siteNav.setFilters(next);
  }

  resetFilters(): void {
    this.siteNav.resetFilters();
    this._syncPendingFiltersWithStored();
  }

  onNodeClick(event: TreeNodeEvent): void {
    if (event.type === 'blast' && this.isMapMode()) {
      this.mapFocus.update(current => ({ blastId: Number(event.id), sequence: (current?.sequence ?? 0) + 1 }));
      return;
    }
    const screenType = this.activeScreen();
    const isSameScreenType =
      (event.type === 'blast'   && screenType === 'blast')   ||
      (event.type === 'project' && screenType === 'project') ||
      (event.type !== 'blast' && event.type !== 'project' && screenType === 'node');
    const preservedTab = isSameScreenType ? this.pageBar.config().activeTabId || null : null;
    // Same screen type: keep the tab and its sub-view (e.g. Holes `view=quality`) for the new node.
    const preservedView = isSameScreenType ? this.route.snapshot.queryParamMap.get('view') : null;
    this.siteNav.navigateFromNodeClick(
      event,
      preservedTab ?? this._firstTabForNodeType(event.type),
      preservedView !== null ? { view: preservedView } : {},
    );
  }

  onBadgeClick(event: TreeNodeEvent): void {
    if (event.type === 'blast') this.openBlast(Number(event.id));
  }

  /** Le tir sélectionné sur la carte est sélectionné dans l'arbre ; à la fermeture de sa popup, on revient à la sélection issue de l'URL. */
  onMapBlastSelectionChange(blastId: number | null): void {
    this.navLeft.setSelectedNodeId(blastId !== null ? `blast:${blastId}` : this.siteNav.selectedNodeId());
  }

  /** Opening a blast from another screen lands on Files ; from a blast, the current tab is kept. */
  openBlast(blastId: number): void {
    const preservedTab = this.activeScreen() === 'blast' ? this.pageBar.config().activeTabId || null : null;
    this.siteNav.navigateFromNodeClick({ id: blastId, type: 'blast' }, preservedTab ?? this._firstTabForNodeType('blast'));
  }

  onSiteCardClick(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.router.navigate(['/site', siteId], {
      queryParams: { tab: SITE_TABS[0].id },
    });
  }

  private _firstTabForNodeType(type: string | undefined): string {
    switch (type) {
      case 'blast':   return 'files';
      case 'project': return PROJECT_TABS[0]!.id;
      default:        return NODE_TABS[0]!.id;
    }
  }

  private _getActionsTemplate(): TemplateRef<any> {
    switch (this.activeScreen()) {
      case 'blast':   return this.pageBarBlastTpl;
      case 'project': return this.pageBarProjectTpl;
      case 'node':    return this.pageBarNodeTpl;
      default:        return this.pageBarSiteTpl;
    }
  }

  private _syncPendingGroupingWithStored(): void {
    const grouping = this.siteNav.grouping();
    this.pendingL1.set(grouping.level1);
    this.pendingL2.set(grouping.level2);
    this.pendingL3.set(grouping.level3);
  }

  private _syncPendingFiltersWithStored(): void {
    const stored = this.siteNav.filters();
    const options = this.siteNav.filterOptions();

    this.pendingActiveProjectsOnly.set(stored.activeProjectsOnly);
    this.pendingActiveBlastsOnly.set(stored.activeBlastsOnly);
    this.pendingProjects.set(clampSelection(stored.projects, options.projects));
    this.pendingZones.set(clampSelection(stored.zones, options.zones));
    this.pendingTypes.set(clampSelection(stored.types, options.types));
    this.pendingStatus.set(stored.status);
    this.pendingLevel.set(stored.level && options.levels.includes(stored.level) ? stored.level : null);
    this.pendingStartDate.set(stored.startDate ?? '');
    this.pendingEndDate.set(stored.endDate ?? '');
  }

  onProjectsChange(projects: SelectOption[]): void {
    this.pendingProjects.set(projects.map(o => o.id as string));
  }

  onZonesChange(zones: SelectOption[]): void {
    this.pendingZones.set(zones.map(o => o.id as string));
  }

  onTypesChange(types: SelectOption[]): void {
    this.pendingTypes.set(types.map(o => o.id as string));
  }
}